import {
  FC,
  useMemo,
  useState,
  useCallback,
} from "react";

import Table from "apps/website/components/feature/Table/Table";
import { themeRootClassMap } from "apps/website/maps/Theme.map";
import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";
import Image, { IImageProps } from "apps/website/components/base/Image/Image";
import SummaryFixedPriceRow from "apps/website/components/form/Summary/SummaryFixedPrice/SummaryFixedPriceRow";
import { getFieldValues } from "@./state";
import { ISummaryItem } from "@forms/schema";
import Column from "apps/website/components/layout/Column/Column";

interface FixedPriceSummaryProps {
  image?: IImageProps;
  flowSlug: string;
}

export const FixedPriceSummary: FC<FixedPriceSummaryProps> = ({ image, flowSlug }) => {
  const [ refreshKey, setRefreshKey ] = useState(0);

  const catNames = useMemo(() => getFieldValues(flowSlug, "catName"), [ flowSlug, refreshKey ]);
  const removedStatus = useMemo(() => getFieldValues(flowSlug, "catPlanRemoved"), [ flowSlug, refreshKey ]);
  const catAllergies = useMemo(() => getFieldValues(flowSlug, "catAllergies"), [ flowSlug, refreshKey ]);

  const items: ISummaryItem[] = useMemo(() => {
    // Calculate number of non-removed cats
    const activeCatsCount = catNames.filter((cat) => !removedStatus
      .find((status) => status.key.linkingId === cat.key.linkingId && status.data.submitValue)).length;

    const isLastCat = activeCatsCount === 1;

    const catItems: ISummaryItem[] = catNames.map((cn) => {
      const planRemoved = !!removedStatus.find((x) => x.key.linkingId === cn.key.linkingId)?.data.submitValue || false;

      return {
        title: planRemoved ? undefined : { value: `28 meals, feeds ${cn.data.displayValue} for 14 days` },
        // Only include removalData if it's not the last cat OR if the cat is already removed
        removalData: (isLastCat && !planRemoved) ? undefined : {
          removeTitle: `Remove ${cn.data.displayValue}'s plan`,
          addTitle: `Add back ${cn.data.displayValue}'s plan`,
          linkingId: cn.key.linkingId,
          planRemoved,
          catName: cn.data.displayValue as string,
        },
      };
    });

    const catsWithoutChickenAllergy = catNames.filter((cat) => {
      // Check if cat plan is removed
      const isCatRemoved = removedStatus
        .find((status) => status.key.linkingId === cat.key.linkingId && status.data.submitValue);

      if (isCatRemoved) {
        return false;
      }

      // Check if cat has chicken allergy
      const hasChickenAllergy = catAllergies.find((allergy) => (
        allergy.key.linkingId === cat.key.linkingId &&
        Array.isArray(allergy.data.submitValue) ? allergy.data.submitValue?.includes("CHICKEN")
          : allergy.data.submitValue === "CHICKEN"
      ));

      return !hasChickenAllergy;
    }).length;

    const baseItems = [
      ...catItems,
      {
        title: { value: "Personalised switching guide" },
      },
      {
        title: { value: "Free delivery" },
      },
      {
        title: { value: "Support from our in-house Vet Nurse" },
      },
    ];

    if (catsWithoutChickenAllergy > 0) {
      baseItems.splice(catItems.length, 0, {
        title: { value: `Free Chicken Sprinkles meal topper (x${catsWithoutChickenAllergy})` },
      });
    }

    return baseItems;
  }, [ catNames, removedStatus, catAllergies ]);

  const totalPrice = useMemo(() => {
    const activeCats = catNames.filter((cat) => !removedStatus
      .find((status) => status.key.linkingId === cat.key.linkingId && status.data.submitValue));
    return activeCats.length * 20;
  }, [ catNames, removedStatus ]);

  const reloadSummary = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  return (
    <Column>
      <div data-component={ FixedPriceSummary.name }>
        { image?.src && (
          <Image image={ image } alt="In your Fresh Feast Trial" />
        ) }
        <Table className={ "w-full border-collapse bg-white" }>
          <tr>
            <th
              className={ `text-left px-2 py-2 ${themeRootClassMap.dark} text-white` }
              data-theme="dark"
              scope="col"
              colSpan={3}
            >
              <div className="flex items-center justify-between">
                <TextSubtitle size={2}>In your Fresh Feast Trial:</TextSubtitle>
              </div>
            </th>
          </tr>
          <>
            <>

              <>
                { items?.map(((item, index) => (
                  <SummaryFixedPriceRow
                    key={ index }
                    item={ item }
                    className="bg-white"
                    reloadSummary={ reloadSummary }
                    flowSlug={ flowSlug }
                  />
                ))) }
              </>
              <>
                <tr className="bg-white">
                  <td colSpan={3} className="px-2">
                    <div className="bg-light-grey px-2 py-2">
                      <div className="flex justify-between items-center">
                        <TextSubtitle size={2}>Total today</TextSubtitle>
                        <TextSubtitle size={2}>£{ totalPrice }</TextSubtitle>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr className="bg-white">
                  <td colSpan={3} className="px-2">
                    <div className="px-2 py-2">
                      <div className="flex justify-between items-center">
                        <TextSubtitle size={4}>Price per meal</TextSubtitle>
                        <TextSubtitle size={4}>£0.71</TextSubtitle>
                      </div>
                    </div>
                  </td>
                </tr>
              </>
            </>
          </>
        </Table>
      </div>
    </Column>
  );
};
